import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { finalize, take, takeUntil } from "rxjs";
import { IIndicatorMap, UIErrorHandlingService } from "@gtmhub/error-handling";
import { localize } from "@gtmhub/localization";
import { IReduxTeam } from "@gtmhub/teams";
import { CurrentUserService, getCurrentUserId } from "@gtmhub/users";
import { IdMap, NameMap } from "@gtmhub/util";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { AssigneesFacade } from "@webapp/assignees/services/assignees/assignees-facade.service";
import { AggregatedMentions, Comment, CommentTarget, UiComment } from "@webapp/comments/models/comments.models";
import { CommentsFacade } from "@webapp/comments/services/comments-facade.service";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { confirmDelete } from "@webapp/shared/modal/confirm";
import { TeamsFacade } from "@webapp/teams/services/teams-facade.service";
import { UiModalService } from "@webapp/ui/modal/services/modal.service";

const COMMENTS_REQUEST_FIELDS: (keyof Comment)[] = [
  "id",
  "createdAt",
  "createdBy",
  "editedBy",
  "mentioned",
  "reactions",
  "seen",
  "targetId",
  "targetTitle",
  "targetType",
  "text",
  "gif",
];

@Component({
  selector: "comments",
  templateUrl: "./comments.component.html",
  styleUrls: ["./comments.component.less"],
  providers: [DestroyService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentsComponent implements OnChanges, OnInit {
  @Input() public target: CommentTarget;
  @Input() public isNewCommentOnTop = false;
  @Input() public placeholder: string;
  @Input() public metadata: { [key: string]: string };
  @Input() public initialMentions: string[];
  @Input() public skipFetchingComments = false;

  @Output() public readonly add: EventEmitter<Comment> = new EventEmitter();
  @Output() public readonly cancel: EventEmitter<void> = new EventEmitter();
  @Output() public readonly remove: EventEmitter<Comment> = new EventEmitter();

  public indicators: IIndicatorMap = {};
  public comments: UiComment[] = [];

  public mentions: AggregatedMentions = {
    removable: [],
    nonRemovable: [],
  };

  public editedCommentId: string;
  public isCurrentUserViewOnly: boolean;
  public currentUserId: string;
  public assignees: IdMap<Assignee>;
  public teams: IdMap<IReduxTeam>;
  public allowedModifyCommentMap: NameMap<boolean> = {};

  public constructor(
    private currentUserService: CurrentUserService,
    private assigneesFacade: AssigneesFacade,
    private commentsFacade: CommentsFacade,
    private destroy$: DestroyService,
    private teamsFacade: TeamsFacade,
    private modalService: UiModalService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private changeDetector: ChangeDetectorRef
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.initialMentions && !changes.initialMentions.isFirstChange()) {
      this.initRemovableMentions();
    }
  }

  public ngOnInit(): void {
    this.assignees = this.assigneesFacade.getAssigneesIdMap();
    this.teams = this.teamsFacade.getTeamsIdMap();
    this.isCurrentUserViewOnly = this.currentUserService.userHasRole("view-only");
    this.currentUserId = getCurrentUserId();

    if (this.skipFetchingComments) {
      this.comments = [];
      this.initRemovableMentions();
    } else {
      this.loadComments();
    }
  }

  public removeMention(mentionId: string): void {
    this.mentions.removable = this.mentions.removable.filter((mention) => mention !== mentionId);
  }

  public removeAllMentions(): void {
    this.mentions.removable = [];
  }

  public onCommentMentionsChanged(mentions: string[]): void {
    this.mentions.nonRemovable = mentions;

    // removes duplicated mention IDs from the removable mentions array
    const nonRemovable = new Set(this.mentions.nonRemovable);
    this.mentions.removable = (this.mentions.removable || []).filter((mentionId) => !nonRemovable.has(mentionId));
  }

  public onCommentCreationCanceled(): void {
    this.cancel.emit();
  }

  public onCommentPosted(comment: Comment): void {
    if (this.isNewCommentOnTop) {
      this.comments.unshift(comment);
    } else {
      this.comments.push(comment);
    }

    this.allowedModifyCommentMap[comment.id] = true;
    this.initRemovableMentions();
    this.add.emit(comment);
  }

  public onCommentEdited(comment: Comment): void {
    this.editedCommentId = null;

    const oldComment = this.comments.find((listComment) => listComment.id === comment.id);
    if (oldComment) {
      Object.assign(oldComment, comment);
      this.initRemovableMentions();
    }
  }

  public closeInlineEditor(): void {
    this.editedCommentId = null;
    this.changeDetector.detectChanges();
  }

  public openCommentDeleteConfirmation(comment: Comment): void {
    const { title, description } = this.generateConfirmDeleteCopy(comment);
    confirmDelete(this.modalService, { title, description }).subscribe(() => {
      this.commentsFacade
        .deleteComment$(comment.id)
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.comments = this.comments.filter((c) => c.id !== comment.id);
            this.initRemovableMentions();
            this.remove.emit(comment);

            this.changeDetector.markForCheck();
          },
          error: (error) => {
            this.uiErrorHandlingService.handleModal(error);
          },
        });
    });
  }

  public openInlineEditor(commentId: string): void {
    this.editedCommentId = commentId;
  }

  private generateConfirmDeleteCopy(commentToBeDeleted: Comment): { title: string; description: string } {
    const targetIsNotKpi = this.target.type !== "kpi_snapshot" && this.target.type !== "kpi_projection";
    const defaultTitle = localize("confirm_delete_comment_title");
    const defaultDescription = localize("this_cant_be_undone");
    if (targetIsNotKpi || this.isNewCommentOnTop) {
      return { title: defaultTitle, description: defaultDescription };
    }

    const commentIndex = this.comments.findIndex((comment) => comment.id === commentToBeDeleted.id);

    if (commentIndex === 0) {
      return { title: defaultTitle, description: `${localize("confirm_delete_comment_and_replies_content")} ${defaultDescription}` };
    } else {
      return { title: localize("confirm_delete_reply_title"), description: defaultDescription };
    }
  }

  private initRemovableMentions(): void {
    const uniqueMentions = new Set<string>();

    // extracts the mention IDs from the thread source (e.g. resource owners)
    (this.initialMentions || []).forEach((id) => {
      if (!this.assignees[id]?.isActive) {
        return;
      }

      // extract corresponding user IDs in case of mentioned teams
      if (this.assignees[id].type === "team" && this.teams[id]) {
        (this.teams[id].members || []).forEach((memberId) => uniqueMentions.add(memberId));
        this.teams[id].manager && uniqueMentions.add(this.teams[id].manager);
      } else {
        uniqueMentions.add(id);
      }
    });

    // extracts mention IDs from all comments in the thread
    (this.comments || []).forEach((comment) => {
      (comment.mentioned || []).forEach((mention) => uniqueMentions.add(mention));
      uniqueMentions.add(comment.createdBy);
    });

    // clear empty entires and current user ID
    const targetMentions = Array.from(uniqueMentions).filter((mention) => mention && mention !== this.currentUserId);

    this.mentions.removable = targetMentions;
  }

  private loadComments(): void {
    this.indicators = { loading: { progress: true } };

    const filter = { fields: COMMENTS_REQUEST_FIELDS };
    const sort = [this.isNewCommentOnTop ? "-createdAt" : "createdAt"];
    const targetDateParams = this.target.date ? { targetDate: this.target.date } : {};
    const queryParams = {
      targetIds: [this.target.id],
      targetType: this.target.type,
      sort,
      ...targetDateParams,
    };

    this.commentsFacade
      .getComments$(filter, queryParams)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          delete this.indicators.loading;
          this.changeDetector.markForCheck();
        })
      )
      .subscribe({
        next: (comments) => {
          this.comments = comments;
          this.allowedModifyCommentMap = this.shouldAllowCommentModify();
          this.initRemovableMentions();
          this.markAsSeenAndDetectFirstUnreadComment();
        },
        error: (error) => {
          this.uiErrorHandlingService.handleModal(error);
        },
      });
  }

  private shouldAllowCommentModify(): NameMap<boolean> {
    return this.comments.reduce((allowedModifyCommentMap, comment) => {
      allowedModifyCommentMap[comment.id] = this.currentUserId === comment.createdBy || this.isCurrentUserAdmin();

      return allowedModifyCommentMap;
    }, {});
  }

  private isCurrentUserAdmin(): boolean {
    return this.currentUserService.userHasRole("admin");
  }

  private markAsSeenAndDetectFirstUnreadComment(): void {
    const commentsTheCurrentUserIsMentionedInAndHasNotSeen = this.comments.filter((comment) => {
      const isMentioned = comment.mentioned?.some((mentionId) => this.currentUserId === mentionId);
      const hasSeen = comment.seen?.some((seenUserId) => this.currentUserId === seenUserId);
      return isMentioned && !hasSeen;
    });

    this.markAllAsSeen(commentsTheCurrentUserIsMentionedInAndHasNotSeen);
    this.enrichTheFirstUnreadComment(commentsTheCurrentUserIsMentionedInAndHasNotSeen);
  }

  private markAllAsSeen(commentsToBeMarkedAsSeen: UiComment[]): void {
    commentsToBeMarkedAsSeen.forEach((comment) => {
      this.commentsFacade
        .markAsSeen$(comment.id)
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe(() => {
          comment.seen = comment.seen || [];
          comment.seen.push(this.currentUserId);

          this.changeDetector.markForCheck();
        });
    });
  }

  private enrichTheFirstUnreadComment(unreadCommentsMentioningCurrentUser: UiComment[]): void {
    if (this.isNewCommentOnTop) {
      return;
    }

    const thereIsACommentToEnrichAndIsNotLeading =
      this.comments.length > 0 && unreadCommentsMentioningCurrentUser.length > 0 && this.comments[0].id !== unreadCommentsMentioningCurrentUser[0].id;
    if (thereIsACommentToEnrichAndIsNotLeading) {
      unreadCommentsMentioningCurrentUser[0].isFirstUnreadByCurrentUser = true;
    }
  }
}
